import { isMobileCheck, makeToast, stringHandle } from "./utils";
import Compressor from "compressorjs";
import Dropzone from "dropzone";
import type { RecDropzoneFiles, RecDropzoneFilesCompressed } from "./types";

/**
 * Initializes image dropzones for file uploads, including image compression and HEIC conversion.
 * @param {string} containerSelector - The CSS selector for the container element.
 * @param {boolean} isSellingListingForm - Indicates if the form is for selling listings.
 * @param {Dropzone[]} dropzoneObjects - An array to store Dropzone instances.
 * @param {RecDropzoneFiles} recDropzoneFiles - An object to store the original files.
 * @param {Object} recDropzoneFilesCompressed - An object to store the compressed files.
 * @param {Object} recDropzonePreloaded - An object to store preloaded files.
 */
export const imageDropzonesInit = (
  containerSelector: string,
  isSellingListingForm: boolean,
  dropzoneObjects: Dropzone[],
  recDropzoneFiles: RecDropzoneFiles,
  recDropzoneFilesCompressed: RecDropzoneFilesCompressed,
  recDropzonePreloaded: Record<string, string>,
) => {
  const container = document.querySelector(containerSelector) as HTMLFormElement | null;
  let recDropzoneFileCount = 0;
  /* Image Compression Logic */
  const bytesToMegaBytes = (bytes: number) => bytes / 1024 ** 2;
  const blobRead = (blob: Blob): Promise<string | undefined> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = function () {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          resolve(undefined);
        }
      };
      reader.onerror = function (error) {
        reject(error);
      };
    });

  const convertObjectToBytes = async (obj: string): Promise<string | undefined> => {
    const blob = await fetch(obj).then((r) => r.blob());
    return await blobRead(blob);
  };

  const imageCompress = async (
    fileKey: string,
    fileOrig: Dropzone.DropzoneFile,
    fileData: File,
    q: number,
    w: number,
    h: number,
  ) => {
    // log all the arguments on separate lines
    let outputQuality = (100 - q) / 100;
    let outputWidth = w * outputQuality;
    let outputHeight = h * outputQuality;
    new Compressor(fileData, {
      quality: outputQuality,
      width: outputWidth,
      height: outputHeight,
      async success(result) {
        let fileNew = fileOrig;
        var url = URL.createObjectURL(result);
        fileNew.dataURL = await convertObjectToBytes(url);
        recDropzoneFilesCompressed[fileKey] = fileNew;
        if (fileOrig.previewElement) {
          fileOrig.previewElement.style.setProperty(
            "background-image",
            fileKey.indexOf("listing-image") < 0
              ? `url(${recDropzoneFilesCompressed[fileKey].dataURL})`
              : `url(${recDropzonePreloaded[fileOrig.name]})`
          );
        }
      },
      async error(err) {
        console.error("imageCompress: ", err);

        //TODO handle compression error
        if (fileOrig.previewElement) {
          fileOrig.previewElement.style.setProperty(
            "background-image",
            fileKey.indexOf("listing-image") < 0
              ? `url(${recDropzoneFilesCompressed[fileKey].dataURL})`
              : `url(${recDropzonePreloaded[fileOrig.name]})`
          );
        }
      },
    });
  };

  const convertHEIC = async (file: Dropzone.DropzoneFile) => {
    if (!file.dataURL) {
      throw new Error("File dataURL is undefined");
    }

    try {
      const currentFile = await fetch(file.dataURL);
      const currentBlob = await currentFile.blob();

      return await heic2any({
        blob: currentBlob,
        toType: "image/jpeg",
        quality: 0.8,
      });
    } catch (error) {
      makeToast("toast-heic-error")
    }
  };

  const dropzones = document.querySelectorAll(
    ".rec-image-dropzone",
  ) as NodeListOf<HTMLDivElement>;
  // The variable below is the max number of images to load into the dropzone at one time. Listings can have up to five but this value determines how many can be loaded at once. There are issues with loading multiple HEIC images so this is set to 1. Users will have to select images one at a time. Issues revolve around the compression/conversion of multiple images and the async operations of Dropzone.
  const maxImagesLoad = 1;
  if (dropzones && dropzones.length > 0) {
    for (var dzi = 0; dzi < dropzones.length; dzi++) {
      var myDropzone = new Dropzone(dropzones[dzi], {
        url: "/upload",
        parallelUploads: 1,
        autoProcessQueue: false,
        maxFilesize: 10,
        paramName: dropzones[dzi].attributes.getNamedItem("data-name")?.value,
        maxThumbnailFilesize: 10,
        thumbnailMethod: "contain",
        maxFiles: maxImagesLoad,
        dictRemoveFile: "&times;",
        addRemoveLinks: true,
        acceptedFiles: ".jpeg,.jpg,.png,.heic",
        hiddenInputContainer: container || undefined,
        init: function () {
          this.on("addedfile", function (file) {
            recDropzoneFileCount++;
          });

          this.on("thumbnail", async (file) => {
            const fileKey =
              file.name.indexOf("listing_image") < 0
                ? file.upload?.uuid || `${file.name}-${Date.now()}`
                : stringHandle(file.name);
            file.previewElement.setAttribute("data-image-key", fileKey);
            let fileToCompress = file;
            //heic image?
            try {
              if (
                fileKey.indexOf("listing-image") < 0 &&
                !(fileKey in recDropzoneFilesCompressed) &&
                file?.type == "image/heic"
              ) {
                const heicBlob = await convertHEIC(file);

                if (heicBlob !== undefined) {
                  // @ts-expect-error: this seems to work dispite the type mistmatch. Original implementation was JS.
                  fileToCompress = new File([heicBlob], file.name, {
                    type: "image/jpeg",
                  });
                  console.log("fileToCompress: ", fileToCompress)

                  blobRead(fileToCompress).then((dataURL) => {
                    if (dataURL !== undefined) {
                      file.dataURL = dataURL;
                      const imgElement = file.previewElement.querySelector(".dz-image-preview img") as HTMLImageElement | null;
                      if (imgElement !== null) {
                        imgElement.src = dataURL;
                      }
                    } else {
                      console.error("HEIC dataURL is undefined");
                    }
                  }
                  );
                } else {
                  this.removeFile(file)
                }
              }
            } catch (err) {
              console.error("HEIC error: ", err);
              makeToast("toast-heic-error")
            }

            //init the file in the main array
            recDropzoneFiles[fileKey] = file;

            //compress image?
            if (
              fileKey.indexOf("listing-image") < 0 &&
              bytesToMegaBytes(file.size) >= 0.5
            ) {
              recDropzoneFilesCompressed[fileKey] = file;
              await imageCompress(
                fileKey,
                file,
                fileToCompress,
                45,
                900,
                1200,
              );
            } else {
              if (file.previewElement) {
                file.previewElement.style.setProperty(
                  "background-image",
                  fileKey.indexOf("listing-image") < 0
                    ? `url(${recDropzoneFiles[fileKey].dataURL})`
                    : `url(${recDropzonePreloaded[file.name]})`
                );
              }
            }

            // Apply alt tag to image
            if (isSellingListingForm) {
              const dzParent = file.previewElement.closest(
                ".rec-image-dropzone",
              );
              const previewImg = file.previewElement.querySelector("img");
              const removeLink = dzParent?.querySelector("a.dz-remove");
              const productTitle = document.getElementById(
                "rec-product-title",
              ) as HTMLInputElement | null;

              if (previewImg) {
                if (productTitle) {
                  previewImg.setAttribute(
                    "alt",
                    `${productTitle.value} - ${dzParent?.getAttribute("data-rec-text") || ""}`,
                  );
                } else {
                  previewImg.setAttribute(
                    "alt",
                    dzParent?.getAttribute("data-rec-text") || "",
                  );
                }
              }

              // Apply aria label to remove image
              if (removeLink) {
                removeLink.setAttribute(
                  "aria-label",
                  `Remove ${dzParent?.getAttribute("data-rec-text")?.toLowerCase() || ""} image`,
                );
              }
            }
            // this removes duplication of files when using image QR upload
            if (recDropzoneFileCount) {
              for (let i = 1; i <= recDropzoneFileCount; i++) {
                const dzKey = `listing-image${i}`;
                if (recDropzoneFiles[dzKey]) {
                  if (recDropzoneFiles[dzKey]["name"] === file.name) {
                    this.files.push(recDropzoneFiles[dzKey]);
                  }
                }
              }
            }

            if (this.files.length > 1) {
              let filesToMove = this.files.length - 1;
              for (let fi = 0; fi <= filesToMove; fi++) {
                const moveFile = this.files[fi];
                if (moveFile) {
                  await this.removeFile(this.files[fi]);
                  for (let thisDZ of dropzoneObjects) {
                    if (
                      thisDZ.files.length == 0 &&
                      thisDZ.element != this.element
                    ) {
                      await thisDZ.displayExistingFile(
                        moveFile,
                        moveFile.dataURL || "",
                        undefined,
                        undefined,
                        false,
                      );
                      await thisDZ.files.push(moveFile);
                      break;
                    }
                  }
                }
              }
            }
          });

          this.on("removedfile", function (file) {
            const fileKey =
              file.name.indexOf("listing_image") < 0
                ? file.upload?.uuid || `${file.name}-${Date.now()}`
                : stringHandle(file.name);
            delete recDropzoneFiles[fileKey];
            delete recDropzonePreloaded[fileKey];
            recDropzoneFileCount--;
            file.previewElement.remove();
          });

          this.on("maxfilesexceeded", (file) => {
            this.removeFile(file);
            makeToast("toast-max-images");
          });

          dropzoneObjects.push(this);
          //apply any currently saved images
          if (
            typeof currentListingImages !== "undefined" &&
            currentListingImages.length > 0
          ) {
            if (currentListingImages.length > dzi) {
              const preloadedImageKey = "listing_image" + (dzi + 1);
              recDropzonePreloaded[preloadedImageKey] =
                currentListingImages[dzi];
              this.displayExistingFile(
                { name: preloadedImageKey, size: 12345 },
                currentListingImages[dzi],
                undefined,
                undefined,
                false,
              );
            }
          }
        },
      });
      dropzones[dzi].classList.add("dropzone");
    }
  }
};
